import React, { useState, useEffect, useMemo } from 'react'
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  useTheme,
  TextField,
  InputAdornment,
  TablePagination,
  Tooltip,
  Checkbox,
  MenuItem,
} from '@material-ui/core'
import { Search as SearchIcon } from '@material-ui/icons'
import { motion, AnimatePresence } from 'framer-motion'
import {
  pageVariants,
  childVariants,
  tableRowVariants,
  StyledTableContainerWithScroll,
  IndicatorChip,
  ValidationAlert,
  EmptyStateBox,
  FileTypeChip,
  SearchFilterContainer,
  SearchFilterRow,
  SearchFieldContainer,
  SomaticModeContainer,
  FilterControlsContainer,
  FilterLabel,
  FilterChipContainer,
  FilterChip,
} from '../styles/StyledComponents'
import { getStatusLabel } from '../../../FileUploadView/utils/fileUtils'
import { fetchCohortFastqPairs, startGenericCohortAnalysis } from '../../../../api/analysis/cohort'
import { useBedFiles } from '../../../../api/file/list'
import { useRnaVocabulary } from '../../../../api/rna'
import { rnaAvailability } from '../../lib/rnaLibrary'
import { IMPORT_FAILED_TEXT } from '../../../../utils/importRefusal'
import BedPanelSelect from './BedPanelSelect'
import FileNameText, { useElementWidth } from '../../../../components/FileNameText'
import { alpha } from '@material-ui/core/styles'
import { TumourRnaCard, TumourRnaChip } from './TumourRnaChoice'

// Sample and file names are shown whole, wrapped at natural break points (after _ . - /).
// FileNameText only middle-truncates a name that would need more than three lines at the
// column's current width, and then shows the full name in a tooltip.
const SAMPLE_NAME_SX = { fontWeight: 600 }

const SampleName = ({ name }) => <FileNameText name={name || 'Unknown'} variant="plain" sx={SAMPLE_NAME_SX} />

// One badge per file: a FASTQ pair shows R1 above R2, so the two reads are easy to tell apart.
const FileNames = ({ names }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
    {names.map((name, index) => (
      <FileNameText key={`${index}-${name}`} name={name || 'Unknown'} />
    ))}
  </Box>
)

// Column widths for the fixed-layout file tables, from the measured width of the table area.
// File Name never gets a width of its own, so it takes all the space the other columns leave.
// In a comfortable table Select, Type and Created stay just wide enough for their content and
// Created never wraps. Below COMFORTABLE_TABLE_WIDTH (tablet, small laptop) the table goes
// compact: tighter cell padding, every column gives up some room, Created may wrap, and File
// Name still keeps the largest share. Widths go on the header cells as inline styles, to win
// over the container's 72px first-column rule.
const COMFORTABLE_TABLE_WIDTH = 830
const TYPE_WIDTH = 136 // widest chip: PACBIO BAM
const CREATED_WIDTH = 176 // e.g. "Mar 31, 2026 18:33" on one line

const getColumnWidths = (areaWidth, pairSelect = false) => {
  // The table is the area minus the container's border and (when shown) its 10px scrollbar.
  const tableWidth = areaWidth > 0 ? areaWidth - 12 : 0
  if (!tableWidth || tableWidth >= COMFORTABLE_TABLE_WIDTH) {
    return {
      compact: false,
      select: pairSelect ? 144 : 92, // T/N or M/P chips vs a radio / checkbox
      sample: '20%',
      type: TYPE_WIDTH,
      created: CREATED_WIDTH,
      createdWhiteSpace: 'nowrap',
    }
  }
  const select = pairSelect ? 116 : 68
  const rest = tableWidth - select
  const type = Math.min(116, Math.round(rest * 0.22))
  const created = Math.min(146, Math.round(rest * 0.26))
  const sample = Math.round((rest - type - created) * 0.42)
  return {
    compact: true,
    select,
    sample,
    type,
    created,
    createdWhiteSpace: created >= 146 ? 'nowrap' : 'normal',
  }
}

// Compact tables trade the container's 32px / 18px cell padding for 16px / 10px.
const COMPACT_TABLE_SX = {
  '& .MuiTable-root .MuiTableHead-root .MuiTableCell-root, & .MuiTable-root .MuiTableBody-root .MuiTableCell-root': {
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  '& .MuiTable-root .MuiTableRow-root .MuiTableCell-root:first-of-type': { paddingLeft: '16px' },
  '& .MuiTable-root .MuiTableRow-root .MuiTableCell-root:last-child': { paddingRight: '16px' },
  // Selected rows draw a 4px left border.
  '& .MuiTable-root .MuiTableRow-root.selected .MuiTableCell-root:first-of-type': { paddingLeft: '12px' },
}

const FileTableHead = ({ widths, selectLabel = 'Select' }) => (
  <TableHead>
    <TableRow>
      <TableCell style={{ width: widths.select }}>{selectLabel}</TableCell>
      <TableCell style={{ width: widths.sample }}>Sample Name</TableCell>
      <TableCell style={{ width: widths.type }}>Type</TableCell>
      <TableCell>File Name</TableCell>
      <TableCell style={{ width: widths.created, whiteSpace: widths.createdWhiteSpace }}>Created</TableCell>
    </TableRow>
  </TableHead>
)

/**
 * True when an uploaded VCF carries SNP/Indel calls — the only variant_type the
 * somatic chain can process.
 *
 * Treats a missing variant_type as SNP/Indel: that is the model default
 * (`VcfFile.variant_type` defaults to 'snp_indel'), and older rows predate the
 * field, so excluding them would silently hide valid files.
 */
const isSnpIndelVcf = (file) => !file?.variant_type || file.variant_type === 'snp_indel'

const StepSelectFile = ({
  analysisType,
  files,
  onFileSelect,
  selectedFiles,
  onSelectionChange,
  carrierConfig,
  cohortSelection: externalCohortSelection,
  onCohortSelectionChange,
  onCohortSelectedRowsChange,
  onCohortBedPanelChange,
  selectedCohortBedPanel,
  technologyType,
  onTechnologyTypeSelect,
}) => {
  const theme = useTheme()
  const [localSelection, setLocalSelection] = useState(selectedFiles)
  const [validationMessage, setValidationMessage] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [fileTypeFilter, setFileTypeFilter] = useState('All')
  const [currentPage, setCurrentPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [somaticAnalysisType, setSomaticAnalysisType] = useState('') // Start empty for somatic selection flow
  // Tumour RNA (roadmap R4.1) is offered only where the analysis start accepts it.
  const { data: rnaVocabulary } = useRnaVocabulary({ enabled: analysisType === 'Somatic' })
  const rnaChoice = useMemo(() => rnaAvailability(rnaVocabulary), [rnaVocabulary])
  useEffect(() => {
    // Learned after the choice was made: back to the choice, which now says why.
    if (!rnaChoice.enabled && somaticAnalysisType === 'tumor-rna') setSomaticAnalysisType('')
  }, [rnaChoice.enabled, somaticAnalysisType])
  const [isDemultiplexed, setIsDemultiplexed] = useState(false)
  // Multiple analysis (cohort) state
  const [cohortSearchSample, setCohortSearchSample] = useState('')
  const [cohortSearchFile, setCohortSearchFile] = useState('')
  const [cohortPage, setCohortPage] = useState(0)
  const [cohortLimit, setCohortLimit] = useState(25)
  const [cohortSortOrder, setCohortSortOrder] = useState('desc')
  const [cohortLoading, setCohortLoading] = useState(false)
  const [cohortError, setCohortError] = useState('')
  const [cohortRows, setCohortRows] = useState([])
  const [cohortTotal, setCohortTotal] = useState(0)
  const [cohortSelection, setCohortSelection] = useState([]) // local fallback when uncontrolled
  const [selectedBedPanel, setSelectedBedPanel] = useState(selectedCohortBedPanel || '')
  // The file tables' column widths follow the width of the area they are laid out in.
  const [tableArea, setTableArea] = useState(null)
  const tableAreaWidth = useElementWidth(tableArea)
  const columnWidths = useMemo(() => getColumnWidths(tableAreaWidth), [tableAreaWidth])
  const pairColumnWidths = useMemo(() => getColumnWidths(tableAreaWidth, true), [tableAreaWidth])

  // Bed files for cohort panel selection
  const {
    query: { data: bedFilesData = [] },
  } = useBedFiles()

  // Controlled/uncontrolled cohort selection support
  const isCohortControlled = typeof onCohortSelectionChange === 'function' && Array.isArray(externalCohortSelection)
  const currentCohortSelection = isCohortControlled ? externalCohortSelection : cohortSelection
  const COHORT_RECOMMENDED = 30
  const COHORT_MAX = 50
  const updateCohortSelection = (updater) => {
    if (isCohortControlled) {
      const next = typeof updater === 'function' ? updater(externalCohortSelection) : updater
      onCohortSelectionChange(next)
      if (typeof onCohortSelectedRowsChange === 'function') {
        const details = cohortRows.filter((r) => next.includes(getCohortRowId(r)))
        onCohortSelectedRowsChange(details)
      }
    } else {
      const next = typeof updater === 'function' ? updater(cohortSelection) : updater
      setCohortSelection(next)
      if (typeof onCohortSelectedRowsChange === 'function') {
        const details = cohortRows.filter((r) => next.includes(getCohortRowId(r)))
        onCohortSelectedRowsChange(details)
      }
    }
  }
  // Helper: support both legacy id and fastqfilepair_id
  const getCohortRowId = (row) => row.fastqfilepair_id || row.id
  const getCohortRowNames = (row) => {
    const r1 = row.fastq1_filename || row.fastq_file_1?.original_name || row.fastq_file_1?.name || ''
    const r2 = row.fastq2_filename || row.fastq_file_2?.original_name || row.fastq_file_2?.name || ''
    return { r1, r2 }
  }
  const getCohortSampleName = (row) => row.sample_name || row.sample || ''
  const getCohortUploadedAt = (row) => row.uploaded_at || row.created_at || row.created || null

  // Sort files by created_at descending (newest first), with null/unknown dates at the back
  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      const dateA = a.uploaded_at ? new Date(a.uploaded_at) : null
      const dateB = b.uploaded_at ? new Date(b.uploaded_at) : null

      // If both dates are valid, compare them normally
      if (dateA && dateB) {
        return dateB - dateA // Newest first
      }

      // If only one date is valid, the valid one comes first
      if (dateA && !dateB) {
        return -1 // A comes first
      }
      if (!dateA && dateB) {
        return 1 // B comes first
      }

      // If both dates are null/unknown, maintain original order
      return 0
    })
  }, [files])

  // Cohort fetcher (server-backed, only for Multiple)
  useEffect(() => {
    if (analysisType !== 'Multiple') return
    const load = async () => {
      setCohortLoading(true)
      setCohortError('')
      try {
        const data = await fetchCohortFastqPairs({
          page: cohortPage,
          limit: cohortLimit,
          sort_order: cohortSortOrder,
          sample_name: cohortSearchSample,
          file_name: cohortSearchFile,
          bed_panel: selectedBedPanel,
        })
        setCohortRows(data.results || [])
        setCohortTotal(data.pagination?.total_count || 0)
      } catch (e) {
        setCohortError('Failed to load cohort files')
      } finally {
        setCohortLoading(false)
      }
    }
    load()
  }, [analysisType, cohortPage, cohortLimit, cohortSortOrder, cohortSearchSample, cohortSearchFile, selectedBedPanel])

  const getFileType = (file) => {
    if (file.file_type === 'vcf') {
      // Surface the VCF sub-type in the Type column: SV/CNV get an indicator, SNP/indel stays plain VCF.
      if (file.variant_type === 'sv') return 'SV VCF'
      if (file.variant_type === 'cnv') return 'CNV VCF'
      return 'VCF'
    }
    if (file.file_type === 'fastq') return 'FASTQ'
    if (file.file_type === 'ont_bam') return 'ONT BAM'
    if (file.file_type === 'pacbio_bam') return 'PacBio BAM'
    return 'Unknown'
  }

  // Base type (ignores the VCF sub-type) for file-type FILTER matching, so an 'SV VCF' / 'CNV VCF'
  // still matches the 'VCF' filter chip.
  const getBaseFileType = (file) => {
    if (file.file_type === 'vcf') return 'VCF'
    if (file.file_type === 'fastq') return 'FASTQ'
    if (file.file_type === 'ont_bam') return 'ONT BAM'
    if (file.file_type === 'pacbio_bam') return 'PacBio BAM'
    return 'Unknown'
  }

  const toggleCohortSelection = (pairId) => {
    updateCohortSelection((prev) => {
      const exists = prev.includes(pairId)
      if (exists) return prev.filter((id) => id !== pairId)
      if (prev.length >= 50) return prev // enforce upper bound 50
      return [...prev, pairId]
    })
  }

  // Helper to get file ID regardless of type
  const getFileId = (file) => file.vcf_id || file.fastq_pair_id || file.ont_bam_id || file.pacbio_bam_id

  // Filter files based on search term and file type
  const filteredFiles = useMemo(() => {
    let filtered = sortedFiles

    // For ONT Long-Read, only show ONT BAM files
    if (technologyType === 'ont-long-read') {
      filtered = filtered.filter((file) => file.file_type === 'ont_bam')
    } else if (technologyType === 'pacbio-long-read') {
      filtered = filtered.filter((file) => file.file_type === 'pacbio_bam')
    } else if (technologyType === 'vcf') {
      // VCF Upload path: only show uploaded VCFs, every variant type. Germline
      // keeps CNV and SV VCFs because they have their own annotation pipelines.
      //
      // The somatic guard below is defensive, not load-bearing: today picking
      // Somatic resets technologyType to '' (AnnotationView index.js), so this
      // branch is germline-only in practice and the condition is always true.
      // It is here so that merging the two VCF routes later cannot silently
      // reintroduce CNV/SV files into a somatic run.
      filtered = filtered.filter((file) => file.vcf_id && (analysisType !== 'Somatic' || isSnpIndelVcf(file)))
    } else if (analysisType === 'Somatic') {
      // For somatic analysis
      if (somaticAnalysisType === 'vcf') {
        // vcf mode: SNP/Indel VCFs ONLY. This is the branch that actually runs
        // for a somatic VCF upload. The somatic chain has no path for a CNV or
        // SV VCF, so offering one starts an analysis that cannot succeed.
        filtered = filtered.filter((file) => file.vcf_id && isSnpIndelVcf(file))
      } else {
        // tumor-only or tumor-normal: fastq pairs only
        filtered = filtered.filter((file) => !file.vcf_id && file.fastq_pair_id)
      }
    } else if (analysisType === 'Carrier') {
      // For carrier analysis, allow both VCF and FASTQ files but exclude ONT BAM and PacBio BAM
      filtered = filtered.filter((file) => file.file_type !== 'ont_bam' && file.file_type !== 'pacbio_bam')
      if (fileTypeFilter !== 'All') {
        filtered = filtered.filter((file) => {
          const fileType = getBaseFileType(file)
          return fileType === fileTypeFilter
        })
      }
    } else {
      // For short-read or default Germline, exclude ONT BAM, PacBio BAM, and VCF
      // (VCF analyses are handled by the dedicated 'vcf' technology type now).
      if (technologyType === 'short-read') {
        filtered = filtered.filter(
          (file) => file.file_type !== 'ont_bam' && file.file_type !== 'pacbio_bam' && !file.vcf_id,
        )
      }
      // Apply file type filter for other analysis types
      if (fileTypeFilter !== 'All') {
        filtered = filtered.filter((file) => {
          const fileType = getBaseFileType(file)
          return fileType === fileTypeFilter
        })
      }
    }

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter((file) => {
        const sampleName = (file.sample || '').toLowerCase()
        const originalName = (file.original_name || '').toLowerCase()
        const searchLower = searchTerm.toLowerCase()

        // Handle different file structures
        let fileNames = ''
        if (file.vcf_id) {
          // VCF file
          fileNames = (file.name || '').toLowerCase()
        } else if (file.fastq_pair_id) {
          // FASTQ pair - search in both R1 and R2 file names
          const r1Name = (file.fastq_file_1?.name || '').toLowerCase()
          const r2Name = (file.fastq_file_2?.name || '').toLowerCase()
          fileNames = `${r1Name} ${r2Name}`
        } else if (file.fastq_file_id) {
          // Single FASTQ file
          fileNames = (file.name || '').toLowerCase()
        } else if (file.ont_bam_id) {
          // ONT BAM file
          fileNames = (file.ont_bam_filename || '').toLowerCase()
        } else if (file.pacbio_bam_id) {
          // PacBio BAM file
          fileNames = (file.pacbio_bam_filename || '').toLowerCase()
        }

        return sampleName.includes(searchLower) || originalName.includes(searchLower) || fileNames.includes(searchLower)
      })
    }

    return filtered
  }, [sortedFiles, searchTerm, fileTypeFilter, analysisType, technologyType])

  // Get paginated files for current page
  const paginatedFiles = useMemo(() => {
    const startIndex = currentPage * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return filteredFiles.slice(startIndex, endIndex)
  }, [filteredFiles, currentPage, rowsPerPage])

  // In Multiple mode, prevent selecting duplicate sample names within the same cohort
  const selectedCohortSampleNames = useMemo(() => {
    const names = new Set()
    cohortRows.forEach((r) => {
      const id = getCohortRowId(r)
      if (currentCohortSelection.includes(id)) {
        const n = getCohortSampleName(r)
        if (n) names.add(n)
      }
    })
    return names
  }, [cohortRows, currentCohortSelection])

  const renderMultipleCohort = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Bed Panel Selection Step */}
      {!selectedBedPanel && (
        <BedPanelSelect
          defaultSelectedName={selectedBedPanel}
          onSelectionChange={(panelName) => {
            setSelectedBedPanel(panelName)
            if (typeof onCohortBedPanelChange === 'function') {
              onCohortBedPanelChange(panelName)
            }
          }}
        />
      )}

      {/* Cohort File Table - Only show after bed panel selection */}
      {selectedBedPanel && (
        <>
          <Box
            sx={{
              mb: 1,
              p: 1,
              textAlign: 'center',
              borderRadius: 1.5,
              border: (t) => `1px solid ${t.palette.divider}`,
              backgroundColor: (t) => t.palette.action.hover,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.1px',
              }}
            >
              Cohort Files: {bedFilesData?.find((b) => b.name === selectedBedPanel)?.visible_name || selectedBedPanel}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                border: (t) => `1px solid ${t.palette.divider}`,
                backgroundColor: (t) => t.palette.background.paper,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color:
                    currentCohortSelection.length > COHORT_RECOMMENDED
                      ? (t) => t.palette.warning.main
                      : (t) => t.palette.text.primary,
                }}
              >
                Selected: {currentCohortSelection.length}
              </Typography>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                / {COHORT_RECOMMENDED} recommended (max {COHORT_MAX})
              </Typography>
            </Box>
          </Box>

          <SearchFilterContainer>
            <SearchFilterRow>
              <SearchFieldContainer>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by sample name"
                  value={cohortSearchSample}
                  onChange={(e) => {
                    setCohortPage(0)
                    setCohortSearchSample(e.target.value)
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </SearchFieldContainer>
              <SearchFieldContainer>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by file name"
                  value={cohortSearchFile}
                  onChange={(e) => {
                    setCohortPage(0)
                    setCohortSearchFile(e.target.value)
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </SearchFieldContainer>
              <FilterControlsContainer>
                <FilterLabel variant="body2">Sort:</FilterLabel>
                <FilterChipContainer>
                  <FilterChip active={cohortSortOrder === 'desc'} onClick={() => setCohortSortOrder('desc')}>
                    Newest
                  </FilterChip>
                  <FilterChip active={cohortSortOrder === 'asc'} onClick={() => setCohortSortOrder('asc')}>
                    Oldest
                  </FilterChip>
                </FilterChipContainer>
              </FilterControlsContainer>
            </SearchFilterRow>
          </SearchFilterContainer>

          <StyledTableContainerWithScroll component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {cohortRows.length === 0 && !cohortLoading ? (
              <EmptyStateBox>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 0.5, color: theme.palette.text.primary, textAlign: 'center' }}
                >
                  No files found for this capture kit
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
                  Try adjusting the search fields above
                  {selectedBedPanel ? ', or go Back to choose another panel.' : '.'}
                </Typography>
              </EmptyStateBox>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: columnWidths.select }}>Select</TableCell>
                      <TableCell style={{ width: columnWidths.sample }}>Sample</TableCell>
                      <TableCell>FASTQ Files</TableCell>
                      <TableCell style={{ width: columnWidths.created, whiteSpace: columnWidths.createdWhiteSpace }}>
                        Created
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cohortRows.map((row) => {
                      const rowId = getCohortRowId(row)
                      const selected = currentCohortSelection.includes(rowId)
                      const sampleName = getCohortSampleName(row) || ''
                      const duplicateTaken = selectedCohortSampleNames.has(sampleName)
                      const rowDisabled = !selected && (currentCohortSelection.length >= COHORT_MAX || duplicateTaken)
                      const { r1, r2 } = getCohortRowNames(row)
                      return (
                        <TableRow
                          key={rowId}
                          className={`${selected ? 'selected' : ''} ${rowDisabled ? 'disabled' : ''}`}
                          onClick={() => {
                            if (!rowDisabled) toggleCohortSelection(rowId)
                          }}
                          onKeyDown={(e) => {
                            if (rowDisabled) return
                            if (e.key === 'Enter' || e.key === ' ') toggleCohortSelection(rowId)
                          }}
                          tabIndex={0}
                          sx={{ cursor: rowDisabled ? 'not-allowed' : 'pointer' }}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selected}
                              onChange={() => toggleCohortSelection(rowId)}
                              color="primary"
                              disabled={rowDisabled}
                            />
                          </TableCell>
                          <TableCell>
                            <SampleName name={getCohortSampleName(row) || '·'} />
                          </TableCell>
                          <TableCell>
                            <FileNames names={[r1, r2]} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: columnWidths.createdWhiteSpace }}>
                            {getCohortUploadedAt(row) ? formatCreationDate(getCohortUploadedAt(row)) : '·'}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={cohortTotal}
                  page={cohortPage}
                  onPageChange={(e, p) => setCohortPage(p)}
                  rowsPerPage={cohortLimit}
                  onRowsPerPageChange={(e) => {
                    setCohortLimit(parseInt(e.target.value, 10))
                    setCohortPage(0)
                  }}
                  rowsPerPageOptions={[10, 25, 30, 50]}
                  labelRowsPerPage="Rows per page:"
                />
              </>
            )}
          </StyledTableContainerWithScroll>
        </>
      )}
    </Box>
  )

  // Reset to first page when filters change, but only if the current page would be invalid
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredFiles.length / rowsPerPage) - 1)
    if (currentPage > maxPage) {
      setCurrentPage(0)
    }
  }, [searchTerm, fileTypeFilter, filteredFiles.length, rowsPerPage, currentPage])

  // Update local selection when props change
  useEffect(() => {
    setLocalSelection(selectedFiles)
  }, [selectedFiles])

  // Handle analysis type changes - auto-filter for somatic and clear invalid selections
  useEffect(() => {
    if (analysisType === 'Somatic') {
      if (somaticAnalysisType === 'vcf') {
        setFileTypeFilter('VCF')
        // Clear FASTQ selections
        const newSelection = { ...localSelection }
        if (newSelection.primary && !files.find((f) => getFileId(f) === newSelection.primary)?.vcf_id) {
          newSelection.primary = null
        }
        newSelection.normal = null // No normal for VCF
        if (newSelection.primary !== localSelection.primary || newSelection.normal !== localSelection.normal) {
          setLocalSelection(newSelection)
        }
      } else {
        setFileTypeFilter('FASTQ')
        // Clear any VCF file selections since they're not allowed for somatic analysis
        const primaryFile = files.find((f) => getFileId(f) === localSelection.primary)
        const normalFile = files.find((f) => getFileId(f) === localSelection.normal)

        const newSelection = { ...localSelection }
        if (primaryFile && (primaryFile.vcf_id || !primaryFile.fastq_pair_id)) {
          newSelection.primary = null
        }
        if (normalFile && (normalFile.vcf_id || !normalFile.fastq_pair_id)) {
          newSelection.normal = null
        }

        if (newSelection.primary !== localSelection.primary || newSelection.normal !== localSelection.normal) {
          setLocalSelection(newSelection)
        }
      }
    } else if (analysisType === 'Carrier') {
      // For carrier analysis, only set default filter if it hasn't been set yet
      // This prevents resetting user's filter choice when selections change
      if (fileTypeFilter === 'All' && !localSelection.primary && !localSelection.normal) {
        setFileTypeFilter('All')
      }
    } else {
      // Reset somatic analysis type when switching away from somatic
      setSomaticAnalysisType('')
    }
  }, [analysisType, files, localSelection, somaticAnalysisType])

  // Handle somatic analysis type changes
  useEffect(() => {
    if (
      analysisType === 'Somatic' &&
      (somaticAnalysisType === 'tumor-only' || somaticAnalysisType === 'tumor-rna' || somaticAnalysisType === 'vcf')
    ) {
      // Clear normal selection when switching to tumor-only, tumour RNA or vcf
      if (localSelection.normal) {
        setLocalSelection({ ...localSelection, normal: null })
      }
    }
  }, [somaticAnalysisType, analysisType, localSelection])

  // Handle Demultiplexed Sample selection (Germline only)
  useEffect(() => {
    if (analysisType === 'Germline') {
      if (isDemultiplexed) {
        setFileTypeFilter('FASTQ')
      }
      setLocalSelection({ primary: null, normal: null })
    }
  }, [analysisType, isDemultiplexed])

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange({
        ...localSelection,
        somaticAnalysisType: analysisType === 'Somatic' ? somaticAnalysisType : undefined,
        isDemultiplexed: analysisType === 'Germline' ? isDemultiplexed : undefined,
      })
    }
  }, [localSelection, onSelectionChange, somaticAnalysisType, analysisType, isDemultiplexed])

  // Validation logic
  useEffect(() => {
    if (analysisType === 'Germline') {
      if (isDemultiplexed) {
        // Count selected files for demultiplexed
        const selectedCount = Object.values(localSelection).filter(Boolean).length
        if (selectedCount >= 2) {
          setValidationMessage('')
        } else {
          setValidationMessage('Please select at least two files for Demultiplexed Germline analysis')
        }
      } else {
        if (localSelection.primary) {
          setValidationMessage('')
        } else {
          setValidationMessage('Please select one file for Germline analysis')
        }
      }
    } else if (analysisType === 'Somatic') {
      if (!somaticAnalysisType) {
        setValidationMessage('Please select a somatic analysis type first')
      } else if (somaticAnalysisType === 'tumor-only') {
        if (localSelection.primary) {
          setValidationMessage('')
        } else {
          setValidationMessage('Please select a Tumor sample for tumor-only analysis')
        }
      } else if (somaticAnalysisType === 'tumor-rna') {
        if (localSelection.primary) {
          setValidationMessage('')
        } else {
          setValidationMessage('Please select the tumour RNA FASTQ pair')
        }
      } else if (somaticAnalysisType === 'tumor-normal') {
        if (localSelection.primary && localSelection.normal) {
          setValidationMessage('')
        } else if (localSelection.primary && !localSelection.normal) {
          setValidationMessage('Please select a Normal sample (N) to complete Tumor/Normal analysis')
        } else if (!localSelection.primary && localSelection.normal) {
          setValidationMessage('Please select a Tumor sample (T) to complete Tumor/Normal analysis')
        } else {
          setValidationMessage('Please select both Tumor (T) and Normal (N) samples for Tumor/Normal analysis')
        }
      } else if (somaticAnalysisType === 'vcf') {
        if (localSelection.primary) {
          setValidationMessage('')
        } else {
          setValidationMessage('Please select a VCF file for analysis')
        }
      }
    }
  }, [localSelection, analysisType, somaticAnalysisType])

  const formatCreationDate = (dateString) => {
    if (!dateString) return 'Unknown'

    const date = new Date(dateString)

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Unknown'
    }

    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    return `${dateStr} ${timeStr}`
  }

  const getFileName = (file) => {
    if (file) {
      return file.sample
    }
    return 'Unknown'
  }
  // Every file name for a row: a FASTQ pair gives R1 and R2, everything else a single name.
  const getDetailedFileNames = (file) => {
    if (file.file_type === 'ont_bam') {
      return [file.ont_bam_filename || 'Unknown']
    }
    if (file.file_type === 'pacbio_bam') {
      return [file.pacbio_bam_filename || 'Unknown']
    }
    if (file.file_type === 'vcf') {
      return [file.vcf_filename || 'Unknown']
    }
    if (file.fastq1_filename && file.fastq2_filename) {
      return [file.fastq1_filename, file.fastq2_filename]
    }
    // Single FASTQ file
    return [file.fastq1_filename || 'Unknown']
  }

  const getFileStatus = (file) => {
    return getStatusLabel(file)
  }

  // A VCF the server refused (refusal_reason) will never verify: it reads as
  // not processed, never as verifying forever.
  const isVcfRefused = (file) => file.file_type === 'vcf' && !file.is_verified && !!file.refusal_reason

  const isFileVerifying = (file) => {
    if (file.file_type === 'vcf') {
      return !file.is_verified && !file.refusal_reason
    }
    if (file.file_type === 'fastq') {
      return !file.is_processed
    }
    if (file.file_type === 'ont_bam') {
      return !file.is_processed
    }
    if (file.file_type === 'pacbio_bam') {
      return !file.is_processed
    }
    return false
  }

  const isFastqUnverified = (file) => {
    return file.file_type === 'fastq' && file.is_processed && !file.is_verified
  }

  const isOntBamUnverified = (file) => {
    return file.file_type === 'ont_bam' && file.is_processed && !file.is_verified
  }

  const isPacbioBamUnverified = (file) => {
    return file.file_type === 'pacbio_bam' && file.is_processed && !file.is_verified
  }

  const isSingleFastqFile = (file) => {
    return file.file_type === 'fastq' && !file.fastq2_filename
  }

  const isFileDisabled = (file) => {
    return (
      isFileVerifying(file) ||
      isVcfRefused(file) ||
      isSingleFastqFile(file) ||
      isFastqUnverified(file) ||
      isOntBamUnverified(file) ||
      isPacbioBamUnverified(file)
    )
  }

  const handleGermlineSelection = (fileId) => {
    const file = files.find((f) => getFileId(f) === fileId)
    if (file && isFileDisabled(file)) {
      return // Don't allow selection of disabled files
    }

    if (isDemultiplexed) {
      // Multi-select logic for demultiplexed
      const currentlySelected = Object.values(localSelection).filter(Boolean)

      // If file is already selected, remove it
      if (isSelected(fileId)) {
        const newSelection = { ...localSelection }
        Object.keys(newSelection).forEach((key) => {
          if (newSelection[key] === fileId) {
            newSelection[key] = null
          }
        })
        setLocalSelection(newSelection)
        return
      }

      // Add the file to the next available slot
      const newSelection = { ...localSelection }

      // Find the first available slot
      if (!newSelection.primary) {
        newSelection.primary = fileId
      } else {
        // Add additional properties for more samples
        let i = 2
        while (true) {
          const key = `sample${i}`
          if (!newSelection[key]) {
            newSelection[key] = fileId
            break
          }
          i++
        }
      }
      setLocalSelection(newSelection)
    } else {
      // Standard single selection
      const newSelection = { primary: fileId, normal: null }
      setLocalSelection(newSelection)
    }
  }

  const handleSomaticSelection = (type, fileId) => {
    const file = files.find((f) => getFileId(f) === fileId)
    if (file && isFileDisabled(file)) {
      return // Don't allow selection of disabled files
    }

    // For tumor/normal analysis, prevent selecting the same file for both roles
    if (somaticAnalysisType === 'tumor-normal') {
      const newSelection = { ...localSelection }

      if (type === 'primary') {
        // If selecting as tumor, clear normal if it's the same file
        if (localSelection.normal === fileId) {
          newSelection.normal = null
        }
        newSelection.primary = fileId
      } else if (type === 'normal') {
        // If selecting as normal, clear tumor if it's the same file
        if (localSelection.primary === fileId) {
          newSelection.primary = null
        }
        newSelection.normal = fileId
      }

      setLocalSelection(newSelection)
    } else {
      // For tumor-only analysis, just set the selection
      const newSelection = {
        ...localSelection,
        [type]: fileId,
      }
      setLocalSelection(newSelection)
    }
  }

  const handleCarrierSelection = (type, fileId) => {
    const file = files.find((f) => getFileId(f) === fileId)
    if (file && isFileDisabled(file)) {
      return // Don't allow selection of disabled files
    }

    const samplesRequired = carrierConfig?.samples_required || 1

    // Special case for 2 samples: handle maternal/paternal selection
    if (samplesRequired === 2 && type) {
      const newSelection = { ...localSelection }

      // If this file is already selected for this type, remove it
      if (newSelection[type] === fileId) {
        newSelection[type] = null
      } else {
        // If this file is selected for the other type, swap it
        if (newSelection.primary === fileId) {
          newSelection.primary = null
        }
        if (newSelection.normal === fileId) {
          newSelection.normal = null
        }
        // Set the file for the selected type
        newSelection[type] = fileId
      }

      setLocalSelection(newSelection)
      return
    }

    // Default case for 1 or more than 2 samples: use checkbox logic
    const currentlySelected = Object.values(localSelection).filter(Boolean)

    // If file is already selected, remove it
    if (isSelected(fileId)) {
      const newSelection = { ...localSelection }
      Object.keys(newSelection).forEach((key) => {
        if (newSelection[key] === fileId) {
          newSelection[key] = null
        }
      })
      setLocalSelection(newSelection)
      return
    }

    // If we haven't reached the sample limit, add the file
    if (currentlySelected.length < samplesRequired) {
      const newSelection = { ...localSelection }

      // Find the first available slot (primary, normal, etc.)
      if (!newSelection.primary) {
        newSelection.primary = fileId
      } else if (!newSelection.normal && samplesRequired > 1) {
        newSelection.normal = fileId
      } else {
        // Add additional properties for more samples if needed
        for (let i = 2; i < samplesRequired; i++) {
          const key = `sample${i + 1}`
          if (!newSelection[key]) {
            newSelection[key] = fileId
            break
          }
        }
      }

      setLocalSelection(newSelection)
    }
  }

  const isSelected = (fileId) => {
    return Object.values(localSelection).includes(fileId)
  }

  const getSelectionType = (fileId) => {
    if (localSelection.primary === fileId) return 'primary'
    if (localSelection.normal === fileId) return 'normal'
    return null
  }

  const isFileSelectedForOtherRole = (fileId) => {
    // Check if this file is selected for the opposite role in tumor/normal analysis
    if (somaticAnalysisType === 'tumor-normal') {
      return (
        (localSelection.primary === fileId && localSelection.normal !== null) ||
        (localSelection.normal === fileId && localSelection.primary !== null)
      )
    }
    return false
  }

  const isValidSelection = () => {
    if (analysisType === 'Germline') {
      if (isDemultiplexed) {
        const selectedCount = Object.values(localSelection).filter(Boolean).length
        return selectedCount >= 2
      }
      return !!localSelection.primary
    } else if (analysisType === 'Somatic') {
      // First, somatic analysis type must be selected
      if (!somaticAnalysisType) return false

      if (somaticAnalysisType === 'tumor-only' || somaticAnalysisType === 'tumor-rna') {
        return !!localSelection.primary
      } else if (somaticAnalysisType === 'tumor-normal') {
        return !!(localSelection.primary && localSelection.normal)
      } else if (somaticAnalysisType === 'vcf') {
        return !!localSelection.primary
      }
    } else if (analysisType === 'Carrier') {
      // For carrier analysis, validate based on samples_required from carrier config
      const samplesRequired = carrierConfig?.samples_required || 1
      const selectedCount = Object.values(localSelection).filter(Boolean).length
      return selectedCount >= samplesRequired
    }
    return false
  }

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setCurrentPage(0)
  }

  const renderGermlineTable = () =>
    isDemultiplexed ? (
      // Multi-select Checkbox Table for Demultiplexed
      <Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <StyledTableContainerWithScroll component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table>
            <FileTableHead widths={columnWidths} />
            <TableBody>
              {paginatedFiles.map((file, index) => {
                const fileId = getFileId(file)
                const selected = isSelected(fileId)
                const isDisabled = isFileDisabled(file)

                return (
                  <TableRow
                    key={fileId}
                    className={`${selected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    component={motion.tr}
                    custom={index}
                    variants={tableRowVariants}
                    initial="initial"
                    animate="animate"
                    whileHover={!isDisabled ? 'hover' : undefined}
                    onClick={(e) => {
                      // Prevent triggering row click when clicking checkbox directly (handled by Checkbox onChange)
                      // checking e.target.type !== 'checkbox' handles the native input, checking data-testid or class might be needed if MUI modifies structure heavily, but usually stopPropagation on Checkbox is cleaner.
                      // Actually, for better UX, row click should toggle too if we want.
                      // Let's allow row click to toggle.
                      if (!isDisabled) handleGermlineSelection(fileId)
                    }}
                    sx={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {/* stopPropagation on the cell or checkbox to prevent double toggle if row has onClick */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected}
                            onChange={() => handleGermlineSelection(fileId)}
                            disabled={isDisabled}
                            color="primary"
                            sx={{
                              '&.Mui-checked': {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label=""
                        sx={{ margin: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <SampleName name={getFileName(file)} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <FileTypeChip
                          fileType={
                            file.file_type === 'ont_bam'
                              ? 'ont_bam'
                              : file.file_type === 'pacbio_bam'
                              ? 'pacbio_bam'
                              : file.vcf_id
                              ? 'vcf'
                              : 'fastq'
                          }
                        >
                          {getFileType(file)}
                        </FileTypeChip>
                        {isSingleFastqFile(file) && <FileTypeChip fileType="single">SINGLE</FileTypeChip>}
                        {isFileVerifying(file) && <FileTypeChip fileType="verifying">VERIFYING</FileTypeChip>}
                        {isVcfRefused(file) && (
                          <Tooltip title={IMPORT_FAILED_TEXT} arrow>
                            <span>
                              <FileTypeChip fileType="unverified">NOT PROCESSED</FileTypeChip>
                            </span>
                          </Tooltip>
                        )}
                        {(isFastqUnverified(file) || isOntBamUnverified(file) || isPacbioBamUnverified(file)) && (
                          <FileTypeChip fileType="unverified">UNVERIFIED</FileTypeChip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FileNames names={getDetailedFileNames(file)} />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          whiteSpace: columnWidths.createdWhiteSpace,
                        }}
                      >
                        {formatCreationDate(file.uploaded_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredFiles.length}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.13)}`,
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
            }}
          />
        </StyledTableContainerWithScroll>
      </Box>
    ) : (
      // Single-select Radio Table for Standard Germline
      <RadioGroup value={localSelection.primary || ''} onChange={(e) => handleGermlineSelection(e.target.value)}>
        <Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <StyledTableContainerWithScroll
            component={Paper}
            sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} // Allow table to scroll
          >
            <Table>
              <FileTableHead widths={columnWidths} />
              <TableBody>
                {paginatedFiles.map((file, index) => {
                  const fileId = getFileId(file)
                  const selected = localSelection.primary === fileId
                  const isDisabled = isFileDisabled(file)
                  const isSingle = isSingleFastqFile(file)

                  const rowContent = (
                    <TableRow
                      key={fileId}
                      className={`${selected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      component={motion.tr}
                      custom={index}
                      variants={tableRowVariants}
                      initial="initial"
                      animate="animate"
                      whileHover={!isDisabled ? 'hover' : undefined}
                      onClick={() => {
                        if (!isDisabled) handleGermlineSelection(fileId)
                      }}
                      onKeyDown={(e) => {
                        if (isDisabled) return
                        if (e.key === 'Enter' || e.key === ' ') handleGermlineSelection(fileId)
                      }}
                      tabIndex={0}
                      sx={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                    >
                      <TableCell>
                        <FormControlLabel
                          value={fileId}
                          control={
                            <Radio
                              color="primary"
                              disabled={isDisabled}
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main,
                                },
                                '& .MuiSvgIcon-root': {
                                  fontSize: '1.2rem',
                                },
                              }}
                            />
                          }
                          label=""
                          sx={{ margin: 0 }}
                        />
                      </TableCell>
                      <TableCell>
                        <SampleName name={getFileName(file)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <FileTypeChip
                            fileType={
                              file.file_type === 'ont_bam'
                                ? 'ont_bam'
                                : file.file_type === 'pacbio_bam'
                                ? 'pacbio_bam'
                                : file.vcf_id
                                ? 'vcf'
                                : 'fastq'
                            }
                          >
                            {getFileType(file)}
                          </FileTypeChip>
                          {isSingleFastqFile(file) && <FileTypeChip fileType="single">SINGLE</FileTypeChip>}
                          {isFileVerifying(file) && <FileTypeChip fileType="verifying">VERIFYING</FileTypeChip>}
                          {isVcfRefused(file) && (
                            <Tooltip title={IMPORT_FAILED_TEXT} arrow>
                              <span>
                                <FileTypeChip fileType="unverified">NOT PROCESSED</FileTypeChip>
                              </span>
                            </Tooltip>
                          )}
                          {(isFastqUnverified(file) || isOntBamUnverified(file) || isPacbioBamUnverified(file)) && (
                            <FileTypeChip fileType="unverified">UNVERIFIED</FileTypeChip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FileNames names={getDetailedFileNames(file)} />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            whiteSpace: columnWidths.createdWhiteSpace,
                          }}
                        >
                          {formatCreationDate(file.uploaded_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )

                  return isSingle ? (
                    <Tooltip
                      key={fileId}
                      title="Single FASTQ files cannot be used for analysis. Please upload the paired R2 file."
                      arrow
                      placement="top"
                    >
                      {rowContent}
                    </Tooltip>
                  ) : (
                    rowContent
                  )
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredFiles.length}
              page={currentPage}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.13)}`,
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: '32px',
                  paddingRight: '32px',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.8rem',
                  color: theme.palette.text.secondary,
                },
                '& .MuiTablePagination-select': {
                  fontSize: '0.8rem',
                },
              }}
            />
          </StyledTableContainerWithScroll>
        </Box>
      </RadioGroup>
    )

  const renderSomaticTable = () => {
    if (somaticAnalysisType === 'tumor-only' || somaticAnalysisType === 'tumor-rna' || somaticAnalysisType === 'vcf') {
      // Tumor-only, tumour RNA or VCF analysis: similar to germline, single selection with radio buttons
      return (
        <RadioGroup value={localSelection.primary || ''} onChange={(e) => handleGermlineSelection(e.target.value)}>
          <Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <StyledTableContainerWithScroll
              component={Paper}
              sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} // Allow table to scroll
            >
              <Table>
                <FileTableHead widths={columnWidths} />
                <TableBody>
                  {paginatedFiles.map((file, index) => {
                    const fileId = getFileId(file)
                    const selected = localSelection.primary === fileId
                    const isDisabled = isFileDisabled(file)
                    const isSingle = isSingleFastqFile(file)

                    const rowContent = (
                      <TableRow
                        key={fileId}
                        className={`${selected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        component={motion.tr}
                        custom={index}
                        variants={tableRowVariants}
                        initial="initial"
                        animate="animate"
                        whileHover={!isDisabled ? 'hover' : undefined}
                        onClick={() => !isDisabled && handleGermlineSelection(fileId)}
                        style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                      >
                        <TableCell>
                          <FormControlLabel
                            value={fileId}
                            control={
                              <Radio
                                color="primary"
                                disabled={isDisabled}
                                sx={{
                                  '&.Mui-checked': {
                                    color: theme.palette.primary.main,
                                  },
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '1.2rem',
                                  },
                                }}
                              />
                            }
                            label=""
                            sx={{ margin: 0 }}
                          />
                        </TableCell>
                        <TableCell>
                          <SampleName name={getFileName(file)} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <FileTypeChip
                              fileType={
                                file.file_type === 'ont_bam'
                                  ? 'ont_bam'
                                  : file.file_type === 'pacbio_bam'
                                  ? 'pacbio_bam'
                                  : file.vcf_id
                                  ? 'vcf'
                                  : 'fastq'
                              }
                            >
                              {getFileType(file)}
                            </FileTypeChip>
                            {isSingleFastqFile(file) && <FileTypeChip fileType="single">SINGLE</FileTypeChip>}
                            {isFileVerifying(file) && <FileTypeChip fileType="verifying">VERIFYING</FileTypeChip>}
                            {isVcfRefused(file) && (
                              <Tooltip title={IMPORT_FAILED_TEXT} arrow>
                                <span>
                                  <FileTypeChip fileType="unverified">NOT PROCESSED</FileTypeChip>
                                </span>
                              </Tooltip>
                            )}
                            {(isFastqUnverified(file) || isOntBamUnverified(file) || isPacbioBamUnverified(file)) && (
                              <FileTypeChip fileType="unverified">UNVERIFIED</FileTypeChip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <FileNames names={getDetailedFileNames(file)} />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              whiteSpace: columnWidths.createdWhiteSpace,
                            }}
                          >
                            {formatCreationDate(file.uploaded_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )

                    return isSingle ? (
                      <Tooltip
                        key={fileId}
                        title="Single FASTQ files cannot be used for analysis. Please upload the paired R2 file."
                        arrow
                        placement="top"
                      >
                        {rowContent}
                      </Tooltip>
                    ) : (
                      rowContent
                    )
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filteredFiles.length}
                page={currentPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                sx={{
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.13)}`,
                  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                  '& .MuiTablePagination-toolbar': {
                    paddingLeft: '32px',
                    paddingRight: '32px',
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.8rem',
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiTablePagination-select': {
                    fontSize: '0.8rem',
                  },
                }}
              />
            </StyledTableContainerWithScroll>
          </Box>
        </RadioGroup>
      )
    } else {
      // Tumor-normal analysis: T/N selection buttons
      return (
        <Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <StyledTableContainerWithScroll
            component={Paper}
            sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} // Allow table to scroll
          >
            <Table>
              <FileTableHead widths={pairColumnWidths} selectLabel="Tumor/Normal" />
              <TableBody>
                {paginatedFiles.map((file, index) => {
                  const fileId = getFileId(file)
                  const selectionType = getSelectionType(fileId)
                  const isDisabled = isFileDisabled(file)
                  const isSingle = isSingleFastqFile(file)

                  const rowContent = (
                    <TableRow
                      key={fileId}
                      className={`${isSelected(fileId) ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      component={motion.tr}
                      custom={index}
                      variants={tableRowVariants}
                      initial="initial"
                      animate="animate"
                      whileHover={!isDisabled ? 'hover' : undefined}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IndicatorChip
                            selected={selectionType === 'primary'}
                            disabled={isDisabled}
                            onClick={!isDisabled ? () => handleSomaticSelection('primary', fileId) : undefined}
                          >
                            T
                          </IndicatorChip>
                          <IndicatorChip
                            selected={selectionType === 'normal'}
                            disabled={isDisabled}
                            onClick={!isDisabled ? () => handleSomaticSelection('normal', fileId) : undefined}
                          >
                            N
                          </IndicatorChip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SampleName name={getFileName(file)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <FileTypeChip
                            fileType={
                              file.file_type === 'ont_bam'
                                ? 'ont_bam'
                                : file.file_type === 'pacbio_bam'
                                ? 'pacbio_bam'
                                : file.vcf_id
                                ? 'vcf'
                                : 'fastq'
                            }
                          >
                            {getFileType(file)}
                          </FileTypeChip>
                          {isSingleFastqFile(file) && <FileTypeChip fileType="single">SINGLE</FileTypeChip>}
                          {isFileVerifying(file) && <FileTypeChip fileType="verifying">VERIFYING</FileTypeChip>}
                          {isVcfRefused(file) && (
                            <Tooltip title={IMPORT_FAILED_TEXT} arrow>
                              <span>
                                <FileTypeChip fileType="unverified">NOT PROCESSED</FileTypeChip>
                              </span>
                            </Tooltip>
                          )}
                          {(isFastqUnverified(file) || isOntBamUnverified(file) || isPacbioBamUnverified(file)) && (
                            <FileTypeChip fileType="unverified">UNVERIFIED</FileTypeChip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FileNames names={getDetailedFileNames(file)} />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            whiteSpace: pairColumnWidths.createdWhiteSpace,
                          }}
                        >
                          {formatCreationDate(file.uploaded_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )

                  return isSingle ? (
                    <Tooltip
                      key={fileId}
                      title="Single FASTQ files cannot be used for analysis. Please upload the paired R2 file."
                      arrow
                      placement="top"
                    >
                      {rowContent}
                    </Tooltip>
                  ) : (
                    rowContent
                  )
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredFiles.length}
              page={currentPage}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.13)}`,
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: '32px',
                  paddingRight: '32px',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.8rem',
                  color: theme.palette.text.secondary,
                },
                '& .MuiTablePagination-select': {
                  fontSize: '0.8rem',
                },
              }}
            />
          </StyledTableContainerWithScroll>
        </Box>
      )
    }
  }

  const renderCarrierTable = () => {
    const samplesRequired = carrierConfig?.samples_required || 1

    // Special case for 2 samples: use Maternal/Paternal selection like tumor/normal
    if (samplesRequired === 2) {
      return (
        <Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <StyledTableContainerWithScroll
            component={Paper}
            sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} // Allow table to scroll
          >
            <Table>
              <FileTableHead widths={pairColumnWidths} selectLabel="Maternal/Paternal" />
              <TableBody>
                {paginatedFiles.map((file, index) => {
                  const fileId = getFileId(file)
                  const selectionType = getSelectionType(fileId)
                  const isDisabled = isFileDisabled(file)

                  const rowContent = (
                    <TableRow
                      key={fileId}
                      className={`${isSelected(fileId) ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      component={motion.tr}
                      custom={index}
                      variants={tableRowVariants}
                      initial="initial"
                      animate="animate"
                      whileHover={!isDisabled ? 'hover' : undefined}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IndicatorChip
                            selected={selectionType === 'primary'}
                            disabled={isDisabled}
                            onClick={!isDisabled ? () => handleCarrierSelection('primary', fileId) : undefined}
                          >
                            M
                          </IndicatorChip>
                          <IndicatorChip
                            selected={selectionType === 'normal'}
                            disabled={isDisabled}
                            onClick={!isDisabled ? () => handleCarrierSelection('normal', fileId) : undefined}
                          >
                            P
                          </IndicatorChip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SampleName name={file.sample || 'Unknown Sample'} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <FileTypeChip fileType={getFileType(file).toLowerCase()}>{getFileType(file)}</FileTypeChip>
                          {isSingleFastqFile(file) && <FileTypeChip fileType="single">SINGLE</FileTypeChip>}
                          {isFileVerifying(file) && <FileTypeChip fileType="verifying">VERIFYING</FileTypeChip>}
                          {isVcfRefused(file) && (
                            <Tooltip title={IMPORT_FAILED_TEXT} arrow>
                              <span>
                                <FileTypeChip fileType="unverified">NOT PROCESSED</FileTypeChip>
                              </span>
                            </Tooltip>
                          )}
                          {(isFastqUnverified(file) || isOntBamUnverified(file) || isPacbioBamUnverified(file)) && (
                            <FileTypeChip fileType="unverified">UNVERIFIED</FileTypeChip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FileNames names={getDetailedFileNames(file)} />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.8rem',
                            whiteSpace: pairColumnWidths.createdWhiteSpace,
                          }}
                        >
                          {formatCreationDate(file.uploaded_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )

                  return rowContent
                })}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={filteredFiles.length}
              page={currentPage}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 20, 50]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                '& .MuiTablePagination-toolbar': {
                  fontSize: '0.8rem',
                  color: theme.palette.text.secondary,
                },
                '& .MuiTablePagination-select': {
                  fontSize: '0.8rem',
                },
              }}
            />
          </StyledTableContainerWithScroll>
        </Box>
      )
    }

    // Default case for 1 or more than 2 samples: use checkboxes
    return (
      <Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <StyledTableContainerWithScroll
          component={Paper}
          sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} // Allow table to scroll
        >
          <Table>
            <FileTableHead widths={columnWidths} />
            <TableBody>
              {paginatedFiles.map((file, index) => {
                const fileId = getFileId(file)
                const selected = isSelected(fileId)
                const isDisabled = isFileDisabled(file)
                const currentlySelected = Object.values(localSelection).filter(Boolean).length

                const rowContent = (
                  <TableRow
                    key={fileId}
                    className={`${selected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    component={motion.tr}
                    custom={index}
                    variants={tableRowVariants}
                    initial="initial"
                    animate="animate"
                    whileHover={!isDisabled ? 'hover' : undefined}
                  >
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected}
                            disabled={isDisabled || (!selected && currentlySelected >= samplesRequired)}
                            color="primary"
                            sx={{
                              '&.Mui-checked': {
                                color: theme.palette.primary.main,
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '1.2rem',
                              },
                            }}
                          />
                        }
                        label=""
                        sx={{ margin: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <SampleName name={file.sample || 'Unknown Sample'} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <FileTypeChip fileType={getFileType(file).toLowerCase()}>{getFileType(file)}</FileTypeChip>
                        {isSingleFastqFile(file) && <FileTypeChip fileType="single">SINGLE</FileTypeChip>}
                        {isFileVerifying(file) && <FileTypeChip fileType="verifying">VERIFYING</FileTypeChip>}
                        {isVcfRefused(file) && (
                          <Tooltip title={IMPORT_FAILED_TEXT} arrow>
                            <span>
                              <FileTypeChip fileType="unverified">NOT PROCESSED</FileTypeChip>
                            </span>
                          </Tooltip>
                        )}
                        {(isFastqUnverified(file) || isOntBamUnverified(file) || isPacbioBamUnverified(file)) && (
                          <FileTypeChip fileType="unverified">UNVERIFIED</FileTypeChip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FileNames names={getDetailedFileNames(file)} />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.8rem',
                          whiteSpace: columnWidths.createdWhiteSpace,
                        }}
                      >
                        {formatCreationDate(file.uploaded_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )

                return rowContent
              })}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredFiles.length}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 20, 50]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
              '& .MuiTablePagination-toolbar': {
                fontSize: '0.8rem',
                color: theme.palette.text.secondary,
              },
              '& .MuiTablePagination-select': {
                fontSize: '0.8rem',
              },
            }}
          />
        </StyledTableContainerWithScroll>
      </Box>
    )
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{
        minHeight: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // Prevent overflow
      }}
    >
      {/* Validation Message at Top */}
      <AnimatePresence>
        {validationMessage && (
          <ValidationAlert
            type={isValidSelection() ? 'success' : 'error'}
            component={motion.div}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isValidSelection() ? theme.palette.success.main : theme.palette.error.main,
              }}
            >
              {isValidSelection() ? 'Ready to proceed!' : validationMessage}
            </Typography>
          </ValidationAlert>
        )}
      </AnimatePresence>

      {/* Somatic Analysis Type Selection - Show First for Somatic */}
      {analysisType === 'Somatic' && !somaticAnalysisType && (
        <Box
          sx={{
            mb: 3,
            p: 3,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            flexShrink: 0, // Prevent this section from shrinking
          }}
        >
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 2 }}>
            Select Somatic Analysis Type
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
            Choose tumour DNA (tumour-only or tumour/normal), tumour RNA, or a pre-called VCF.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }}
              onClick={() => setSomaticAnalysisType('tumor-only')}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                Tumor-Only Analysis
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Analyze tumor samples independently without comparison to normal tissue.
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                Requires: 1 FASTQ file
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }}
              onClick={() => setSomaticAnalysisType('tumor-normal')}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                Tumor/Normal Analysis
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Compare tumor against matched normal samples to identify somatic mutations.
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                Requires: 2 FASTQ files (Tumor + Normal)
              </Typography>
            </Box>
            <TumourRnaCard availability={rnaChoice} onSelect={() => setSomaticAnalysisType('tumor-rna')} />
            <Box
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }}
              onClick={() => setSomaticAnalysisType('vcf')}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                VCF Analysis
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Analyze pre-called variants from a VCF file.
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                Requires: 1 VCF file
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Technology Type Selection - Show for Germline when no technology selected */}
      {analysisType === 'Germline' && !technologyType && (
        <Box
          sx={{
            mb: 3,
            p: 3,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 2 }}>
            Select Sequencing Technology
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
            Choose the sequencing technology used to generate your data.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }}
              onClick={() => onTechnologyTypeSelect('short-read')}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                Paired-End Sequencing
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                WES/WGS and targeted panels from FASTQ.
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                Supports: FASTQ
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }}
              onClick={() => onTechnologyTypeSelect('ont-long-read')}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                ONT Long-Read Sequencing
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Oxford Nanopore Technology long-read BAM files.
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                Supports: BAM
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }}
              onClick={() => onTechnologyTypeSelect('pacbio-long-read')}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                PacBio HiFi Long-Read Sequencing
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                PacBio HiFi long-read BAM files.
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                Supports: BAM
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }}
              onClick={() => onTechnologyTypeSelect('vcf')}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                VCF Analysis
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Pre-called SNP/indel, CNV, or SV variants from a VCF file.
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                Supports: VCF
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* File Selection - Show After Analysis Type is Selected */}
      {((analysisType === 'Germline' && technologyType) ||
        (analysisType === 'Somatic' && somaticAnalysisType) ||
        (analysisType === 'Carrier' && carrierConfig) ||
        analysisType === 'Multiple') && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {/* Carrier Analysis Header - Show Above Search for Carrier */}
          {analysisType === 'Carrier' && carrierConfig && (
            <Box
              sx={{
                mb: 3,
                p: 3,
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
                flexShrink: 0, // Prevent header from shrinking
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  {carrierConfig?.panel_title}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.5,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.19)}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  >
                    {carrierConfig?.samples_required === 2 ? (
                      <>
                        {localSelection.primary ? 'M' : '-'}/{localSelection.normal ? 'P' : '-'} selected
                      </>
                    ) : (
                      <>
                        {Object.values(localSelection).filter(Boolean).length}/{carrierConfig?.samples_required || 1}{' '}
                        selected
                      </>
                    )}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.875rem',
                  lineHeight: 1.4,
                }}
              >
                {carrierConfig?.samples_required === 2 ? (
                  <>
                    Please select <strong>Maternal (M)</strong> and <strong>Paternal (P)</strong> carrier samples for
                    analysis.
                    {Object.values(localSelection).filter(Boolean).length >= 2 && (
                      <span style={{ color: theme.palette.success.main, marginLeft: 8, fontWeight: 600 }}>
                        Selection completed
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    Please select <strong>{carrierConfig?.samples_required || 1}</strong> sample
                    {(carrierConfig?.samples_required || 1) !== 1 ? 's' : ''} for carrier screening analysis.
                    {Object.values(localSelection).filter(Boolean).length >= (carrierConfig?.samples_required || 1) && (
                      <span style={{ color: theme.palette.success.main, marginLeft: 8, fontWeight: 600 }}>
                        Selection completed
                      </span>
                    )}
                  </>
                )}
              </Typography>
            </Box>
          )}

          {/* Unified Search and Filter Bar (not used for Multiple) */}
          {analysisType !== 'Multiple' && (
            <SearchFilterContainer sx={{ flexShrink: 0, flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              {' '}
              {/* Prevent search bar from shrinking */}
              {analysisType === 'Germline' &&
                technologyType !== 'ont-long-read' &&
                technologyType !== 'pacbio-long-read' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isDemultiplexed}
                        onChange={(e) => setIsDemultiplexed(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Demultiplexed Sample
                      </Typography>
                    }
                    sx={{ ml: 0.5, mb: 0 }}
                  />
                )}
              <SearchFilterRow style={{ width: '100%' }}>
                {/* Search Field */}
                <SearchFieldContainer>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by sample name, file name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: '1.1rem' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </SearchFieldContainer>

                {/* Somatic Analysis Type Selection */}
                {analysisType === 'Somatic' && somaticAnalysisType && (
                  <SomaticModeContainer>
                    <FilterLabel variant="body2">Mode:</FilterLabel>
                    <FilterChipContainer>
                      <FilterChip
                        active={somaticAnalysisType === 'tumor-only'}
                        onClick={() => setSomaticAnalysisType('tumor-only')}
                      >
                        Tumor-Only
                      </FilterChip>
                      <FilterChip
                        active={somaticAnalysisType === 'tumor-normal'}
                        onClick={() => setSomaticAnalysisType('tumor-normal')}
                      >
                        Tumor/Normal
                      </FilterChip>
                      <TumourRnaChip
                        availability={rnaChoice}
                        active={somaticAnalysisType === 'tumor-rna'}
                        onSelect={() => setSomaticAnalysisType('tumor-rna')}
                      />
                      <FilterChip active={somaticAnalysisType === 'vcf'} onClick={() => setSomaticAnalysisType('vcf')}>
                        VCF
                      </FilterChip>
                    </FilterChipContainer>
                  </SomaticModeContainer>
                )}

                {/* Filter Controls - hidden for ONT/PacBio Long-Read since only their BAM files are shown */}
                {technologyType !== 'ont-long-read' && technologyType !== 'pacbio-long-read' && (
                  <FilterControlsContainer>
                    <FilterLabel variant="body2">Type:</FilterLabel>
                    <FilterChipContainer>
                      {(analysisType === 'Somatic'
                        ? somaticAnalysisType === 'vcf'
                          ? ['VCF']
                          : ['FASTQ']
                        : ['All', 'VCF', 'FASTQ']
                      ).map((filterType) => {
                        const isDisabled = analysisType === 'Germline' && isDemultiplexed && filterType !== 'FASTQ'
                        return (
                          <FilterChip
                            key={filterType}
                            active={fileTypeFilter === filterType}
                            onClick={() => !isDisabled && setFileTypeFilter(filterType)}
                            style={{
                              opacity: isDisabled ? 0.5 : 1,
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {filterType}
                          </FilterChip>
                        )
                      })}
                    </FilterChipContainer>
                  </FilterControlsContainer>
                )}
              </SearchFilterRow>
            </SearchFilterContainer>
          )}

          {/* File Selection Table */}
          <Box
            ref={setTableArea}
            sx={{ flex: 1, minHeight: 0, overflow: 'hidden', ...(columnWidths.compact ? COMPACT_TABLE_SX : {}) }}
          >
            {/* Allow table to scroll */}
            <AnimatePresence mode="wait">
              {analysisType === 'Multiple' ? (
                renderMultipleCohort()
              ) : files.length > 0 ? (
                <motion.div key={analysisType} variants={childVariants} initial="initial" animate="animate" exit="exit">
                  {analysisType === 'Germline'
                    ? renderGermlineTable()
                    : analysisType === 'Carrier'
                    ? renderCarrierTable()
                    : renderSomaticTable()}
                </motion.div>
              ) : (
                <motion.div variants={childVariants} initial="initial" animate="animate">
                  <EmptyStateBox>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: theme.palette.text.primary,
                        fontSize: '1.1rem',
                      }}
                    >
                      No files available for selection
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem',
                      }}
                    >
                      Please upload files before proceeding with the analysis
                    </Typography>
                  </EmptyStateBox>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Box>
      )}
    </motion.div>
  )
}

export default StepSelectFile
